/** Межвкладочная шина событий платформы P4M (BroadcastChannel). */
import {localHandlers} from "./localHandlers";
import {remoteHandlers} from "./remoteHandlers";
import {CHANNEL_NAME, type BroadcastEvent} from "./EventsBroadcastChannel.constants";
import type {
  BroadcastMessage,
  DeferredBroadcastItem,
  EventsBroadcastChannelOptions,
  EventHandlersMap
} from "./EventsBroadcastChannel.types";

export const DEFAULT_PENDING_STORAGE_KEY = "p4m.pendingBroadcasts";

const DEFAULT_DEBOUNCE_MS = 1000;
const DEFAULT_LOG_PREFIX = "[EventsBroadcastChannel]";
const TAB_ID_STORAGE_KEY = "p4m.eventsBroadcastChannel.tabId";
const PROCESSED_IDS_SUFFIX = ".processedIds";
const MAX_PENDING_QUEUE = 50;
const MAX_PROCESSED_IDS = 20;

const parseJson = <T>(raw: string | null, fallback: T): T => {
  if (!raw) {
    return fallback;
  }

  try {
    return JSON.parse(raw) as T;
  } catch {
    return fallback;
  }
};

const isDeferredItem = <TEvent extends string>(
  value: unknown
): value is DeferredBroadcastItem<TEvent> => {
  if (typeof value !== "object" || value === null) {
    return false;
  }

  const item = value as DeferredBroadcastItem<TEvent>;
  return typeof item.id === "string" &&
    typeof item.scheduledAt === "number" &&
    typeof item.event === "string" &&
    (item.sourceTabId === undefined || typeof item.sourceTabId === "string");
};

const getTabId = (): string => {
  let tabId = sessionStorage.getItem(TAB_ID_STORAGE_KEY);

  if (!tabId) {
    tabId = crypto.randomUUID();
    sessionStorage.setItem(TAB_ID_STORAGE_KEY, tabId);
  }

  return tabId;
};

/**
 * Межвкладочная шина на BroadcastChannel.
 *
 * `send` — сразу. `schedule` + `flushDeferred` — перед navigation; flush после возврата.
 */
export class EventsBroadcastChannel<TEvent extends string> {
  private channel: BroadcastChannel | null = null;
  private readonly lastHandledTimestampByEvent = new Map<TEvent, number>();
  private readonly tabId = getTabId();

  constructor(
    private readonly channelName: string,
    private readonly handlers: EventHandlersMap<TEvent>,
    private readonly options: EventsBroadcastChannelOptions<TEvent> = {}
  ) {}

  /**
   * Сохраняет событие в очередь sessionStorage перед уходом со страницы.
   * Выполняется после вызова flushDeferred.
   * @param {string} event тип события
   * @param {*} [payload] данные события
   */
  schedule(event: TEvent, payload?: unknown): void {
    const queue = this.readQueue();
    queue.push({
      id: crypto.randomUUID(),
      scheduledAt: Date.now(),
      sourceTabId: this.tabId,
      event,
      payload
    });

    if (queue.length > MAX_PENDING_QUEUE) {
      queue.splice(0, queue.length - MAX_PENDING_QUEUE);
    }

    this.saveQueue(queue);
  }

  /**
   * @return {DeferredBroadcastItem[]} отложенные события текущей вкладки,
   * ещё не отправленные через flush
   */
  getScheduledEvents(): DeferredBroadcastItem<TEvent>[] {
    const processedIds = this.getProcessedIds();
    return this.readQueue().filter(
      (item) => this.isOwnScheduledItem(item) && !processedIds.has(item.id)
    );
  }

  /** Отправляет отложенные события текущей вкладки. Повторный вызов безопасен. */
  flushDeferred(): void {
    const queue = this.readQueue();
    const pending = this.getScheduledEvents();

    if (pending.length === 0) {
      return;
    }

    const pendingIds = new Set(pending.map((item) => item.id));
    this.saveQueue(queue.filter((item) => !pendingIds.has(item.id)));
    this.logDebug("отправляем отложенные события:", pending);
    void this.sendScheduled(pending);
  }

  /**
   * Немедленно отправляет событие в другие вкладки и вызывает localHandlers текущей.
   * @param {string} event тип события
   * @param {*} [payload] данные события
   */
  async send(event: TEvent, payload?: unknown): Promise<void> {
    const message: BroadcastMessage<TEvent> = {
      event,
      timestamp: Date.now(),
      sourceTabId: this.tabId,
      payload
    };

    this.logDebug("отправлено событие:", message);

    const channel = new BroadcastChannel(this.channelName);
    channel.postMessage(message);
    channel.close();

    await this.invokeLocalHandler(message);
  }

  /**
   * Подписывается на события других вкладок.
   * @return {Function} функция отписки
   */
  listen(): () => void {
    this.channel = new BroadcastChannel(this.channelName);

    const onMessage = (event: MessageEvent<BroadcastMessage<TEvent>>) => {
      this.handleMessage(event.data);
    };

    this.channel.addEventListener("message", onMessage);

    return () => {
      this.channel?.removeEventListener("message", onMessage);
      this.channel?.close();
      this.channel = null;
    };
  }

  /**
   * Отправляет отложенные события.
   * @param {DeferredBroadcastItem[]} items очередь событий
   */
  private async sendScheduled(items: DeferredBroadcastItem<TEvent>[]): Promise<void> {
    const failed: DeferredBroadcastItem<TEvent>[] = [];

    for (const item of items) {
      try {
        await this.send(item.event, item.payload);
        this.markProcessed(item.id);
      } catch (error) {
        this.logError("ошибка отправки отложенного события:", item.event, error);
        failed.push(item);
      }
    }

    if (failed.length > 0) {
      this.saveQueue([...failed, ...this.readQueue()]);
    }
  }

  private get pendingStorageKey(): string {
    return this.options.pendingStorageKey ?? DEFAULT_PENDING_STORAGE_KEY;
  }

  private get processedIdsStorageKey(): string {
    return `${this.pendingStorageKey}${PROCESSED_IDS_SUFFIX}`;
  }

  /** @return {DeferredBroadcastItem[]} очередь событий */
  private readQueue(): DeferredBroadcastItem<TEvent>[] {
    const parsed = parseJson<unknown>(sessionStorage.getItem(this.pendingStorageKey), null);

    if (Array.isArray(parsed)) {
      const validItems: DeferredBroadcastItem<TEvent>[] = [];

      for (const value of parsed) {
        if (isDeferredItem<TEvent>(value)) {
          validItems.push(value);
        }
      }

      return validItems;
    }

    if (isDeferredItem<TEvent>(parsed)) {
      return [parsed];
    }

    return [];
  }

  /**
   * Сохраняет очередь событий в sessionStorage.
   * @param {DeferredBroadcastItem[]} queue очередь событий
   */
  private saveQueue(queue: DeferredBroadcastItem<TEvent>[]): void {
    if (queue.length === 0) {
      sessionStorage.removeItem(this.pendingStorageKey);
      return;
    }

    sessionStorage.setItem(this.pendingStorageKey, JSON.stringify(queue));
  }

  /**
   * Получает идентификаторы событий, которые были обработаны.
   * @return {Set<string>} идентификаторы событий, которые были обработаны
   */
  private getProcessedIds(): Set<string> {
    const ids = parseJson<string[]>(sessionStorage.getItem(this.processedIdsStorageKey), []);
    return new Set(Array.isArray(ids) ? ids : []);
  }

  /**
   * Отмечает событие как обработанное.
   * @param {string} id идентификатор события
   */
  private markProcessed(id: string): void {
    const ids = Array.from(this.getProcessedIds());
    ids.push(id);

    if (ids.length > MAX_PROCESSED_IDS) {
      ids.splice(0, ids.length - MAX_PROCESSED_IDS);
    }

    sessionStorage.setItem(this.processedIdsStorageKey, JSON.stringify(ids));
  }

  private get debounceMs(): number {
    return this.options.debounceMs ?? DEFAULT_DEBOUNCE_MS;
  }

  private get logPrefix(): string {
    return this.options.logPrefix ?? DEFAULT_LOG_PREFIX;
  }

  /** Запись очереди без sourceTabId — legacy от других модулей; считаем своей вкладкой. */
  private isOwnScheduledItem(item: DeferredBroadcastItem<TEvent>): boolean {
    return !item.sourceTabId || item.sourceTabId === this.tabId;
  }

  /**
   * Проверяет, является ли событие дубликатом.
   * @param {string} event тип события
   * @param {number} timestamp время события
   * @return {boolean} true, если событие является дубликатом
   */
  private isDuplicate(event: TEvent, timestamp: number): boolean {
    const lastHandled = this.lastHandledTimestampByEvent.get(event) ?? 0;

    if (timestamp - lastHandled < this.debounceMs) {
      return true;
    }

    this.lastHandledTimestampByEvent.set(event, timestamp);
    return false;
  }

  /**
   * Вызывает обработчик события на текущей вкладке.
   * @param {BroadcastMessage<TEvent>} message сообщение
   */
  private async invokeLocalHandler(message: BroadcastMessage<TEvent>): Promise<void> {
    const handler = this.options.localHandlers?.[message.event];
    if (!handler) {
      return;
    }

    this.logDebug("обработка события на текущей вкладке:", message);
    await handler(message);
  }

  private handleMessage(message: BroadcastMessage<TEvent>): void {
    const isInvalid = !message?.event || !message?.timestamp || !message?.sourceTabId;
    const isOwnMessage = message?.sourceTabId === this.tabId;

    if (isInvalid || isOwnMessage || this.isDuplicate(message.event, message.timestamp)) {
      return;
    }

    const handler = this.handlers[message.event];
    if (!handler) {
      this.logError("нет обработчика для события:", message.event);
      return;
    }

    this.logDebug("получено событие:", message);
    void Promise.resolve(handler(message)).catch((error) => {
      this.logError("ошибка обработчика события:", message.event, error);
    });
  }

  private logDebug(...args: unknown[]): void {
    // eslint-disable-next-line no-console
    console.debug(this.logPrefix, ...args);
  }

  private logError(...args: unknown[]): void {
    // eslint-disable-next-line no-console
    console.error(this.logPrefix, ...args);
  }
}

export {CHANNEL_NAME, Events, type BroadcastEvent} from "./EventsBroadcastChannel.constants";

export const eventsBroadcastChannel = new EventsBroadcastChannel<BroadcastEvent>(
    CHANNEL_NAME,
    remoteHandlers,
    {
      logPrefix: "[eventsBroadcastChannel]",
      localHandlers: localHandlers
    }
);
